import React from 'react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import ReportingFrameworks from 'src/shared/components/reporting-frameworks/ReportingFrameworks';
import ExploreButton from 'src/shared/components/explore-button/ExploreButton';
import IucnLogo from 'src/shared/components/iucn-logo';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useProject from 'src/shared/hooks/useProject';
import { useIsWithheldFromUser } from 'src/shared/hooks/useProductLicensing';
import { ProductKey } from 'src/shared/types/engine/products';
import { getTotalSpeciesCount, getInvasiveSpeciesCount, getRedListSpeciesCount } from 'src/shared/helpers/taxonomySummaryHelpers';
import SpeciesMiniMap from 'src/app/maps/views/species-map/components/species-mini-map/SpeciesMiniMap';
import { LeafyGreen, CircleAlert } from 'lucide-react';
import { useSiteKeyMetricsSpeciesQuery } from '../state/api/siteSummaryGraphSlice';
import useSiteSummaryAnalytics from '../hooks/useSiteSummaryAnalytics';
import styles from '../SiteSummary.module.scss';

const SiteSpeciesHealthIndicators = () => {
    const navigate = useAppNavigation();
    const { trackExploreButtonClick } = useSiteSummaryAnalytics();

    const { currentProjectDetails, isEDnaProject } = useProject();
    const isEdnaWithheld = useIsWithheldFromUser(ProductKey.EDNA);
    const showsEdnaResults = isEDnaProject && !isEdnaWithheld;

    const { currentData: speciesMapSummary } = useSiteKeyMetricsSpeciesQuery(
        {
            projectId: currentProjectDetails?.projectId || '',
            samples: { habitatAssay: [], area: [] },
        },
        { skip: !currentProjectDetails?.projectId || !showsEdnaResults }
    );

    const taxonomySummary = speciesMapSummary?.taxonomySummary;

    const totalSpeciesCount = React.useMemo(() => getTotalSpeciesCount(taxonomySummary), [taxonomySummary]);
    const invasiveSpeciesCount = React.useMemo(() => getInvasiveSpeciesCount(taxonomySummary), [taxonomySummary]);
    const redListSpeciesCount = React.useMemo(() => getRedListSpeciesCount(taxonomySummary), [taxonomySummary]);

    if (!showsEdnaResults) {
        return null;
    }

    const handleExploreButtonClick = (section: string, navigateTo: () => void) => {
        trackExploreButtonClick({ section: section });
        navigateTo();
    };

    return (
        <div className='grid grid-cols-3 gap-3'>
            <div className='col-span-2 flex flex-col gap-3'>
                <div className={`text-xl ${styles.sectionTitle}`}>Species detections</div>
                <SummaryCard
                    containerClasses={`gap-3 p-5 ${styles.totalSpeciesCard}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <LeafyGreen className='text-primary' />
                        </div>
                    }
                    titleExplainerSlug={{ defaultSlug: 'total-species', explainerSlug: '' }}
                    cardTitle='Total species'
                    cardBody={
                        <div className='flex flex-col'>
                            <div className='text-[42px] text-grey-80'>{totalSpeciesCount}</div>
                            <div className='text-sm text-grey-100'>Total species detected at this site</div>
                        </div>
                    }
                    action={
                        <ExploreButton
                            sectionName='species-map'
                            onClick={() => handleExploreButtonClick('species_health_total_species', () => navigate.toSampleMetricsTable())}
                        />
                    }
                />

                <div className='flex gap-3'>
                    <SummaryCard
                        containerClasses={`gap-3 p-5 ${styles.invasiveSpeciesCard}`}
                        cardIcon={
                            <div className={styles.iconGradient}>
                                <CircleAlert className='text-red-100' />
                            </div>
                        }
                        titleExplainerSlug={{ defaultSlug: 'invasive-species', explainerSlug: '' }}
                        cardTitle='Invasive species'
                        cardBody={
                            <div className='flex flex-col'>
                                <div className='text-[42px] text-grey-80'>{invasiveSpeciesCount}</div>
                                <div className='text-sm text-grey-100'>Total invasive species</div>
                            </div>
                        }
                        action={
                            <ExploreButton
                                sectionName='invasive-species'
                                onClick={() =>
                                    handleExploreButtonClick('species_health_invasive_species', () =>
                                        navigate.toDashboard({ elementId: 'invasive-species-table' })
                                    )
                                }
                            />
                        }
                    />

                    <SummaryCard
                        containerClasses={`gap-3 p-5 ${styles.iucnCard}`}
                        cardIcon={
                            <div className={styles.iconGradient}>
                                <IucnLogo />
                            </div>
                        }
                        titleExplainerSlug={{ defaultSlug: 'iucn-status', explainerSlug: '' }}
                        cardTitle='IUCN Red List species'
                        cardBody={
                            <div className='flex flex-col'>
                                <div className='text-[42px] text-grey-80'>{redListSpeciesCount}</div>
                                <div className='text-sm text-grey-100'>Red list species detected</div>
                            </div>
                        }
                        action={
                            <ExploreButton
                                sectionName='iucn-red-list-species'
                                onClick={() =>
                                    handleExploreButtonClick('species_health_iucn_red_list_species', () =>
                                        navigate.toDashboard({ elementId: 'iucn-red-list-species-table' })
                                    )
                                }
                            />
                        }
                    />
                </div>
            </div>

            <div className='col-span-1 flex flex-col gap-3'>
                <ReportingFrameworks />
                <div className={`rounded-lg shadow-portfolio-summary-card overflow-hidden ${styles.mapCard}`}>
                    <div className='w-full h-[340px]'>
                        <SpeciesMiniMap />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SiteSpeciesHealthIndicators;
